import { ComboBox, IDropdownOption, Stack } from '@fluentui/react';
import { ColumnDto, DataverseConfiguration, DataverseIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useMemo, useRef } from 'react';
import { DATAVERSE_RESOURCE_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface IAssignmentPaneProps {
    config: DataverseConfiguration;
    dataverseTables: IDropdownOption<ColumnDto>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
}

export const ResourcePane = (props: IAssignmentPaneProps) => {
    const { dataverseUrl, resourceTableLogicalName } = props.config;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: resourceOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_RESOURCE_COLUMNS, dataverseUrl, resourceTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: resourceTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && resourceTableLogicalName),
        suspense: true,
    });

    const booleanResourceOptions = useMemo(
        () => [
            { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } } as IDropdownOption<ColumnDto>,
            ...resourceOptions.filter(opt => opt.key !== DESELECT_KEY && opt.data?.attributeType === 'Boolean'),
        ],
        [resourceOptions],
    );

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Dataverse Resource Table"
                allowFreeform
                autoComplete="on"
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.resourceTableLogicalName = opt.key as string;
                                integration.configuration.resourceTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.resourceTableLogicalName = '';
                                integration.configuration.resourceTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={resourceTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Id"
                allowFreeform
                autoComplete="on"
                required
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Name"
                required
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceNameColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceNameColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Email"
                required
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceEmailColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceEmailColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="UPN"
                required
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceUserPrincipalNameColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceUserPrincipalNameColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Azure Id"
                required
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceAzureIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceAzureIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="User Number"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceUserNumberColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceUserNumberColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Job Title"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceJobTitleColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceJobTitleColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Department"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceDepartmentColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceDepartmentColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Daily Capacity"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceCapacityColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceCapacityColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Earliest Available"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceStartDateColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceStartDateColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Latest Available"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceEndDateColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceEndDateColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Work Type"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceUserWorkTypeIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceUserWorkTypeIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Calendar Id"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceCalendarIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceCalendarIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Approver Id"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceApproverIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceApproverIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Is Not Active"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceIsNotActiveColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceIsNotActiveColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Auto Approve"
                allowFreeform
                autoComplete="on"
                options={booleanResourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceAutoApproveColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={booleanResourceOptions.length <= 1}
                selectedKey={props.config.resourceAutoApproveColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Sync Flag"
                allowFreeform
                autoComplete="on"
                options={resourceOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.resourceSyncColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceOptions.length <= 0}
                selectedKey={props.config.resourceSyncColumn ?? ''}
                onRenderOption={onRenderOption}
            />
        </Stack>
    );
};
